import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft, Users, Award, Star, Radio, MousePointerClick, MailOpen } from 'lucide-react';
import {
  AdvertisersManager,
  SupportersManager,
  FeaturedPartnersManager,
} from './EntityManager';
import { IncidentWatchAccessManager } from './IncidentWatchAccess';
import { PropertyClicksManager } from './PropertyClicks';
import { PropertyEnquiriesManager } from './PropertyEnquiries';

/**
 * Unified admin hub for the three sponsor-ish collections that were
 * previously three separate admin pages:
 *   • /admin/advertisers     → Advertisers
 *   • /admin/supporters      → Supporters
 *   • /admin/featured-partners → Featured Partners
 *
 * Each tab renders the existing `EntityManager` component for that
 * collection — no schema migration, no data churn. The old routes still
 * work so any bookmarked links keep functioning.
 */
const TABS = [
  {
    key: 'advertisers',
    label: 'Advertisers',
    icon: Users,
    blurb: 'Paid ad slots that appear in the homepage carousel.',
    Manager: AdvertisersManager,
  },
  {
    key: 'supporters',
    label: 'Supporters',
    icon: Award,
    blurb: 'Community sponsors & backers surfaced in the supporters carousel.',
    Manager: SupportersManager,
  },
  {
    key: 'featured',
    label: 'Featured Partners',
    icon: Star,
    blurb: 'Spotlight sponsors — appear in the featured partner block with a testimonial.',
    Manager: FeaturedPartnersManager,
  },
  {
    key: 'incident-watch',
    label: 'Incident Watch access',
    icon: Radio,
    blurb: 'Who can see the full Incident Watch feed — paying subscribers, plus anyone you give free access to.',
    Manager: IncidentWatchAccessManager,
  },
  {
    key: 'property-clicks',
    label: 'Agent click-throughs',
    icon: MousePointerClick,
    blurb: 'How much traffic the for-sale pages send to each selling agent — useful evidence when negotiating.',
    Manager: PropertyClicksManager,
  },
  {
    key: 'property-enquiries',
    label: 'Buyer enquiries',
    icon: MailOpen,
    blurb: 'Buyers who asked about a home through the site — pass each one to the agent marketing it.',
    Manager: PropertyEnquiriesManager,
  },
];

const PartnersHub = () => {
  const [active, setActive] = useState('advertisers');
  const Current = TABS.find((t) => t.key === active) || TABS[0];
  const ManagerComponent = Current.Manager;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-4">
          <Link to="/admin/dashboard">
            <Button variant="outline" size="sm" data-testid="partners-hub-back">
              <ArrowLeft size={16} className="mr-2" />Dashboard
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Partners &amp; Sponsors</h1>
            <p className="text-xs text-slate-500 mt-0.5">
              One place for advertisers, supporters and featured partners. Data lives in the same three collections as before — this hub just consolidates the UI.
            </p>
          </div>
        </div>

        {/* Tab strip */}
        <div className="max-w-7xl mx-auto px-6 flex gap-1 overflow-x-auto" data-testid="partners-hub-tabs">
          {TABS.map((t) => {
            const Icon = t.icon;
            const isActive = active === t.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setActive(t.key)}
                data-testid={`partners-tab-${t.key}`}
                className={
                  'inline-flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 transition-colors ' +
                  (isActive
                    ? 'border-cyan-500 text-cyan-700'
                    : 'border-transparent text-slate-500 hover:text-slate-900')
                }
              >
                <Icon size={15} /> {t.label}
              </button>
            );
          })}
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 pt-4">
        <p className="text-sm text-slate-600 italic mb-4" data-testid="partners-tab-blurb">
          {Current.blurb}
        </p>
      </div>

      {/* Existing EntityManager mounts, keeping their internal header/list layout.
          We render inside a div so their own bg-slate-50 doesn't stack. */}
      <div className="-mt-2" data-testid={`partners-tab-body-${active}`}>
        <ManagerComponent />
      </div>
    </div>
  );
};

export default PartnersHub;
